// Bounded polling for GINI Artifact versions.
//
// This is POLLING, not push, and the naming throughout says so. Artifact SSE
// delivery does not exist (the artifact-ready emitter is in-process only), so
// there is no stream to subscribe to and no "realtime" claim to make. The
// server echoes `pushDelivery: false` on every response as the same
// statement from the other side.
//
// Every rule the polling loop has to satisfy is encoded here rather than in
// the component, so a future component can't accidentally drop one:
//
//   - bounded interval with exponential backoff, capped
//   - hard maximum consecutive failures, after which the loop STOPS (it does
//     not spin forever against a broken endpoint)
//   - cancellation on unmount, via AbortController
//   - visibility-aware pause (a backgrounded tab polls nothing)
//   - stale-response suppression by monotonic sequence number
//   - de-duplication of artifact versions by (artifactId, artifactVersion)
//   - explicit manual refresh that bypasses the backoff

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GiniArtifactManifest, GiniHandoffArtifactLineage } from '@open-design/contracts/gini-platform';

import { fetchHandoffArtifacts } from '../providers/gini-handoff';

export const GINI_HANDOFF_POLL_BASE_MS = 4_000;
export const GINI_HANDOFF_POLL_MAX_MS = 60_000;
/** After this many consecutive failures the loop stops and surfaces a manual refresh instead of hammering. */
export const GINI_HANDOFF_POLL_MAX_FAILURES = 5;

export type GiniHandoffPollingPhase = 'idle' | 'polling' | 'paused' | 'stopped';

export interface GiniHandoffArtifactsState {
  artifacts: GiniHandoffArtifactLineage[];
  phase: GiniHandoffPollingPhase;
  lastPolledAt: string | null;
  error: string | null;
  /** True once the loop gave up after GINI_HANDOFF_POLL_MAX_FAILURES. */
  exhausted: boolean;
  refresh: () => void;
}

/**
 * Merges a poll result into the known set, keyed by (artifactId, version).
 * Versions are immutable by contract, so a version already seen is never
 * replaced — this is what makes a duplicated or re-ordered response
 * harmless. Exported for direct unit testing.
 */
export function mergeArtifactLineages(
  known: readonly GiniHandoffArtifactLineage[],
  incoming: readonly GiniHandoffArtifactLineage[],
): GiniHandoffArtifactLineage[] {
  const byArtifact = new Map<string, Map<number, GiniArtifactManifest>>();
  const order: string[] = [];

  for (const source of [known, incoming]) {
    for (const lineage of source) {
      let versions = byArtifact.get(lineage.artifactId);
      if (!versions) {
        versions = new Map<number, GiniArtifactManifest>();
        byArtifact.set(lineage.artifactId, versions);
        order.push(lineage.artifactId);
      }
      for (const manifest of lineage.versions) {
        // First writer wins: an immutable version never changes, so a later
        // duplicate carries no new information.
        if (!versions.has(manifest.artifactVersion)) versions.set(manifest.artifactVersion, manifest);
      }
    }
  }

  return order.map((artifactId) => ({
    artifactId,
    versions: [...(byArtifact.get(artifactId) ?? new Map<number, GiniArtifactManifest>()).values()].sort(
      (a, b) => a.artifactVersion - b.artifactVersion,
    ),
  }));
}

/** Exponential backoff with a hard ceiling. Exported for direct unit testing. */
export function pollDelayMs(consecutiveFailures: number): number {
  const delay = GINI_HANDOFF_POLL_BASE_MS * 2 ** consecutiveFailures;
  return Math.min(delay, GINI_HANDOFF_POLL_MAX_MS);
}

function documentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

export function useGiniHandoffArtifacts(handoffId: string | null, options: { enabled?: boolean } = {}): GiniHandoffArtifactsState {
  const enabled = options.enabled ?? true;
  const [artifacts, setArtifacts] = useState<GiniHandoffArtifactLineage[]>([]);
  const [phase, setPhase] = useState<GiniHandoffPollingPhase>('idle');
  const [lastPolledAt, setLastPolledAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Monotonic request id. A response whose id is not the latest issued is
  // discarded — this is what prevents a slow earlier poll from overwriting a
  // newer result after the two cross on the wire.
  const sequenceRef = useRef(0);
  const failuresRef = useRef(0);

  const refresh = useCallback(() => {
    // A manual refresh is an explicit user action, so it clears the failure
    // budget and revives an exhausted loop.
    failuresRef.current = 0;
    setExhausted(false);
    setRefreshNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !handoffId) {
      setPhase('idle');
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    async function tick(): Promise<void> {
      if (cancelled) return;

      // Visibility-aware pause: a hidden tab does no work at all. It
      // re-checks on a short fixed cadence rather than subscribing, so the
      // resume path stays identical to the normal path.
      if (documentHidden()) {
        setPhase('paused');
        timer = setTimeout(() => void tick(), GINI_HANDOFF_POLL_BASE_MS);
        return;
      }

      setPhase('polling');
      const sequence = ++sequenceRef.current;
      const result = await fetchHandoffArtifacts(handoffId as string, controller.signal);
      if (cancelled) return;
      // Stale-response suppression.
      if (sequence !== sequenceRef.current) return;

      if (result.ok) {
        failuresRef.current = 0;
        setError(null);
        setArtifacts((prev) => mergeArtifactLineages(prev, result.data.artifacts));
        setLastPolledAt(result.data.polledAt);
      } else {
        failuresRef.current += 1;
        setError(result.message);
        if (failuresRef.current >= GINI_HANDOFF_POLL_MAX_FAILURES) {
          setExhausted(true);
          setPhase('stopped');
          return; // Terminal: no further timer is scheduled.
        }
      }

      timer = setTimeout(() => void tick(), pollDelayMs(failuresRef.current));
    }

    void tick();

    // Resume immediately when the tab becomes visible again rather than
    // waiting out the pending pause interval.
    const onVisibility = () => {
      if (!documentHidden() && !cancelled) {
        if (timer !== undefined) clearTimeout(timer);
        void tick();
      }
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      controller.abort();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [handoffId, enabled, refreshNonce]);

  return useMemo(
    () => ({ artifacts, phase, lastPolledAt, error, exhausted, refresh }),
    [artifacts, phase, lastPolledAt, error, exhausted, refresh],
  );
}
